import { query, useLocation } from "@solidjs/router";
import { getBackendURL } from "@storytell/client/src/getBaseUrl";
import type { Component } from "solid-js";
import { createResource, Suspense } from "solid-js";
import { getAppBaseUrl } from "~/lib/getAppBaseUrl";
import { SharedChatMeta } from "./SharedChatMeta";

interface MetaData {
  title: string;
}

/**
 * Cached fetch for shared chat metadata.
 * Using SolidStart's cache() ensures proper SSR data loading.
 */
const getSharedChatMeta = query(async (token: string): Promise<MetaData | null> => {
  "use server";
  try {
    const baseUrl = getBackendURL();
    const url = `${baseUrl}/public-chats/${token}/meta`;
    const response = await fetch(url);
    if (!response.ok) return null;
    return (await response.json()) as MetaData;
  } catch {
    return null;
  }
}, "shared-chat-meta");

/**
 * Extract the token from a /shared/chat/:token route path.
 */
const extractTokenFromPath = (pathname: string): string | null => {
  const match = pathname.match(/^\/shared\/chat\/([^/]+)$/);
  return match ? match[1] : null;
};

/**
 * SharedChatSSRMeta - Renders meta tags for shared chat routes during SSR.
 *
 * This component must be rendered OUTSIDE of ClientSideSplashScreen
 * so that meta tags are included in the server-rendered HTML.
 *
 * It checks the current route and only renders meta tags for /shared/chat/:token routes.
 */
export const SharedChatSSRMeta: Component = () => {
  const location = useLocation();
  const token = extractTokenFromPath(location.pathname);

  // Early return if not on a shared chat route
  if (!token) return null;

  const url = `${getAppBaseUrl()}/shared/chat/${token}`;
  const sectionParam = location.query.section;
  const section = Array.isArray(sectionParam) ? sectionParam[0] : sectionParam;

  return (
    <Suspense>
      <SharedChatMetaFetcher token={token} url={url} section={section} />
    </Suspense>
  );
};

/**
 * Inner component that fetches and renders meta tags.
 * Using createAsync with deferStream: false ensures SSR waits for the data.
 */
const SharedChatMetaFetcher: Component<{ token: string; url: string; section?: string }> = (props) => {
  const [meta] = createResource(() => getSharedChatMeta(props.token), { deferStream: true });

  return <SharedChatMeta title={meta()?.title} section={meta() ? props.section : undefined} url={props.url} />;
};
